import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getIntentTree,
  type Intent,
  type IntentFlow,
  type IntentPage,
  type IntentTree,
  type PageCategory,
} from "../lib/api";
import { CrawlTabs } from "../components/CrawlTabs";

/**
 * Intent Tree — derived "what can a user actually *do* in this app"
 * view. Each intent (auth, commerce, account, content, errors, shell,
 * other) groups one or more flows; each flow is either a multi-step
 * journey from the flow-graph or a single solo page under that category.
 *
 * Implemented as a vertical accordion: intents collapse/expand, flows
 * within expand to show their ordered page list + deep links back to
 * the Crawl Map and Test Generator.
 */
export function IntentTreePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [tree, setTree] = useState<IntentTree | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getIntentTree(id)
      .then((t) => {
        if (!cancelled) setTree(t);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!id) return <div>Missing crawl id.</div>;
  if (error) return <ErrorPane message={error} />;
  if (!tree) return <LoadingPane crawlId={id} />;

  return (
    <div>
      <header className="mb-4">
        <div className="flex items-center gap-3 text-xs text-slate-500 font-mono">
          <Link to="/" className="hover:text-slate-300">
            dashboard
          </Link>
          <span>/</span>
          <Link to={`/crawls/${encodeURIComponent(id)}`} className="hover:text-slate-300">
            {id}
          </Link>
          <span>/</span>
          <span className="text-slate-300">intents</span>
        </div>
        <h1 className="text-2xl font-semibold mt-2 truncate">{tree.targetUrl}</h1>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-400">
          <Pill label="intents" value={String(tree.stats.intentCount)} />
          <Pill label="flows" value={String(tree.stats.flowCount)} />
          <Pill label="pages" value={String(tree.stats.pageCount)} />
          {tree.stats.otherFlowCount > 0 && (
            <Pill label="unclassified" value={String(tree.stats.otherFlowCount)} />
          )}
        </div>
      </header>

      <CrawlTabs crawlId={id} active="intents" />

      {tree.intents.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          {tree.intents.map((intent) => (
            <IntentCard key={intent.id} intent={intent} crawlId={id} />
          ))}
        </div>
      )}
    </div>
  );
}

function IntentCard({
  intent,
  crawlId,
}: {
  intent: Intent;
  crawlId: string;
}): JSX.Element {
  // Auto-expand the first intent bucket — most crawls have ≤3 buckets so
  // collapsing everything on mount makes the page feel empty.
  const [open, setOpen] = useState<boolean>(true);
  const color = intent.category
    ? colorForCategory(intent.category)
    : "bg-slate-600/20 text-slate-400 border-slate-600/30";

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-slate-800/40 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`inline-flex items-center text-[10px] uppercase tracking-wide border rounded px-2 py-0.5 ${color}`}
          >
            {intent.id}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-100 truncate">
              {intent.title}
            </div>
            <div className="text-xs text-slate-500 truncate">
              {intent.description}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-mono text-[11px] text-slate-400">
            {intent.flows.length} {intent.flows.length === 1 ? "flow" : "flows"}
          </span>
          <Chevron open={open} />
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-800 divide-y divide-slate-800">
          {intent.flows.map((flow) => (
            <FlowRow key={flow.name} flow={flow} crawlId={crawlId} />
          ))}
        </div>
      )}
    </section>
  );
}

function FlowRow({
  flow,
  crawlId,
}: {
  flow: IntentFlow;
  crawlId: string;
}): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const pageCount = flow.pages.length;
  // If a journey flow corresponds to a generated Selenium/Playwright test,
  // the emitter uses the same `flow.name` camelCase → PascalCase. We link
  // into the test generator page; the user then picks the matching file.
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-slate-800/40 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Chevron open={open} />
          <div className="min-w-0">
            <div className="font-mono text-[12px] text-slate-200 truncate">
              {flow.name}
            </div>
            <div className="text-[11px] text-slate-400 truncate mt-0.5">
              {flow.labels.join(" → ")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {flow.solo ? (
            <span className="text-[10px] uppercase tracking-wide text-slate-500">
              solo
            </span>
          ) : (
            <span className="font-mono text-[10px] text-indigo-300">
              score {flow.score.toFixed(1)}
            </span>
          )}
          <span className="font-mono text-[10px] text-slate-500">
            {pageCount} {pageCount === 1 ? "page" : "pages"}
          </span>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-800/60 bg-slate-950/30 px-4 py-3">
          <ol className="space-y-2">
            {flow.pages.map((p, i) => (
              // Composite key — same URL can legitimately appear twice in
              // a flow when the user revisits a page (form retries, SPA
              // re-routes). Bare `key={p.url}` would warn + drop the
              // second row on re-render.
              <li key={`${i}::${p.url}`}>
                <PageRow page={p} index={i} crawlId={crawlId} />
              </li>
            ))}
          </ol>
          {!flow.solo && (
            <div className="mt-3 text-[11px]">
              <Link
                to={`/crawls/${encodeURIComponent(crawlId)}/tests`}
                className="text-indigo-400 hover:text-indigo-300"
              >
                view generated test for this flow →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PageRow({
  page,
  index,
  crawlId,
}: {
  page: IntentPage;
  index: number;
  crawlId: string;
}): JSX.Element {
  const color = colorForCategory(page.category);
  const pct = Math.round(page.confidence * 100);
  return (
    <div className="flex items-start gap-3">
      <span className="shrink-0 font-mono text-[10px] text-slate-500 pt-0.5 w-4 text-right">
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center text-[10px] border rounded px-1.5 py-0.5 ${color}`}
          >
            <span className="font-medium">{page.label}</span>
            <span className="opacity-60 font-mono ml-1">{pct}</span>
          </span>
          <div className="text-sm text-slate-200 truncate">
            {page.title || "(untitled)"}
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="font-mono text-[11px] text-slate-500 truncate">
            {page.url}
          </span>
          <Link
            to={`/crawls/${encodeURIComponent(crawlId)}#${encodeURIComponent(page.url)}`}
            className="text-[11px] text-indigo-400 hover:text-indigo-300 shrink-0"
          >
            map →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Chevron({ open }: { open: boolean }): JSX.Element {
  return (
    <span
      className={`inline-block text-slate-500 transition-transform ${
        open ? "rotate-90" : ""
      }`}
      aria-hidden
    >
      ▶
    </span>
  );
}

function colorForCategory(cat: PageCategory): string {
  switch (cat) {
    case "auth":
      return "bg-indigo-500/15 text-indigo-300 border-indigo-500/30";
    case "commerce":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    case "content":
      return "bg-sky-500/15 text-sky-300 border-sky-500/30";
    case "account":
      return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "shell":
      return "bg-slate-600/20 text-slate-400 border-slate-600/30";
    case "errors":
      return "bg-rose-500/15 text-rose-300 border-rose-500/30";
  }
}

function Pill({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-slate-800 bg-slate-900/60 px-2 py-1">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <span className="font-mono text-slate-200 text-[11px]">{value}</span>
    </span>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 px-5 py-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-2 w-2 rounded-full bg-slate-600" />
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-200">
            No intents derived
          </div>
          <p className="mt-1 text-xs text-slate-400">
            The intent-tree builder found no labelled pages, journeys, or
            page snapshots to categorise. Re-run the crawl on a site that
            matches a PageRule (e.g. <code>saucedemo.com</code>,
            <code>demoblaze.com</code>) to populate this view.
          </p>
        </div>
      </div>
    </div>
  );
}

function LoadingPane({ crawlId }: { crawlId: string }): JSX.Element {
  return (
    <div>
      <CrawlTabs crawlId={crawlId} active="intents" />
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
        Loading intent tree…
      </div>
    </div>
  );
}

function ErrorPane({ message }: { message: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
      <div className="font-medium">Failed to load intent tree</div>
      <div className="font-mono text-xs mt-1 text-rose-300/80">{message}</div>
    </div>
  );
}
